import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Button, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentIcon from '@mui/icons-material/Payment';
import { usePagosInmuebles } from '../../hooks/usePagosInmuebles';
import { IDetalleCasaPago, ITrxCasa, IPagoInmuebleListItem } from '../../models/pagosInmuebles';

interface DetalleCasaPagoProps {
    casaItem: IPagoInmuebleListItem;
    onBack: () => void;
    onRegistrarPago: () => void;
}

export const DetalleCasaPago = ({ casaItem, onBack, onRegistrarPago }: DetalleCasaPagoProps) => {
    const { getDetalleCasa, getHistorialCasa } = usePagosInmuebles();
    const [detalle, setDetalle] = useState<IDetalleCasaPago | null>(null);
    const [historial, setHistorial] = useState<ITrxCasa[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const [det, hist] = await Promise.all([
                getDetalleCasa(casaItem.casa),
                getHistorialCasa(casaItem.casa),
            ]);
            if (det) setDetalle(det);
            if (hist) setHistorial(hist);
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [casaItem.casa]);

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f9', pb: 4 }}>
            <Box sx={{
                background: 'linear-gradient(135deg, #013467 0%, #0a4a8a 100%)',
                px: 2, py: 3, mb: 3, display: 'flex', alignItems: 'center', gap: 1,
            }}>
                <IconButton onClick={onBack} sx={{ color: '#fff' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                        {casaItem.num_Casa} - {casaItem.nombre}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                        Detalle de cuenta
                    </Typography>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ px: 2 }}>
                    <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2.5, mb: 3, boxShadow: '0 2px 8px rgba(1,52,103,0.08)' }}>
                        <Typography variant="subtitle2" sx={{ color: '#013467', fontWeight: 700, mb: 1.5 }}>
                            Datos del inmueble
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                            <Box><Typography variant="caption" color="text.secondary">NIT</Typography><Typography variant="body2">{detalle?.nit || '-'}</Typography></Box>
                            <Box><Typography variant="caption" color="text.secondary">Saldo</Typography><Typography variant="body2" fontWeight={700}>Q. {(detalle?.saldo ?? 0).toFixed(2)}</Typography></Box>
                            <Box><Typography variant="caption" color="text.secondary">Email</Typography><Typography variant="body2">{detalle?.email || '-'}</Typography></Box>
                            <Box><Typography variant="caption" color="text.secondary">Teléfono</Typography><Typography variant="body2">{detalle?.telefono || '-'}</Typography></Box>
                            <Box><Typography variant="caption" color="text.secondary">Contacto</Typography><Typography variant="body2">{detalle?.nombre_Contacto || '-'}</Typography></Box>
                            <Box><Typography variant="caption" color="text.secondary">Email Contacto</Typography><Typography variant="body2">{detalle?.email_Contacto || '-'}</Typography></Box>
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<PaymentIcon />}
                            onClick={onRegistrarPago}
                            sx={{ mt: 2.5, textTransform: 'none', fontWeight: 600, backgroundColor: '#013467', '&:hover': { backgroundColor: '#0a4a8a' } }}
                        >
                            Registrar pago
                        </Button>
                    </Box>

                    <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2.5, boxShadow: '0 2px 8px rgba(1,52,103,0.08)', overflowX: 'auto' }}>
                        <Typography variant="subtitle2" sx={{ color: '#013467', fontWeight: 700, mb: 1.5 }}>
                            Historial de movimientos
                        </Typography>
                        {historial.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">Sin movimientos registrados.</Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fecha</TableCell>
                                        <TableCell>Servicio</TableCell>
                                        <TableCell align="right">Cargo</TableCell>
                                        <TableCell align="right">Abono</TableCell>
                                        <TableCell align="right">Saldo</TableCell>
                                        <TableCell>Nota</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {historial.map(row => (
                                        <TableRow key={row.linea}>
                                            <TableCell>{row.fecha}</TableCell>
                                            <TableCell>{row.nom_Servicio}</TableCell>
                                            <TableCell align="right">{row.cargo ? `Q. ${row.cargo.toFixed(2)}` : '-'}</TableCell>
                                            <TableCell align="right">{row.abono ? `Q. ${row.abono.toFixed(2)}` : '-'}</TableCell>
                                            <TableCell align="right">Q. {row.saldo.toFixed(2)}</TableCell>
                                            <TableCell>{row.nota}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};
